import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Constants } from "appConstants";
import BackofficeApplicationListFilterForm, {
	ApplicationListFilters
} from "components/BackofficeApplicationListFilterForm";
import BackofficeList, { FilterTools } from "components/BackofficeList";
import {
	ApplicationListStatusDisplay,
	asCellRenderer,
	BasicValueDisplay,
	DeadlineDisplay,
	NaiveDateValueDisplay,
	NameValueDisplay,
	ProjectProgressStatusDisplay
} from "components/BackofficeList/modules/ValueDisplays";
import TooltipWrapper from "components/TooltipWrapper";
import { ErrorHelper, TableHelper } from "helpers";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import { TableColumnDef } from "helpers/TableHelper";
import usePagination from "hooks/usePagination";
import useStabilizedState from "hooks/useStabilizedState";
import useUrlFilters from "hooks/useUrlFilters";
import { InternalUserStatusLabel } from "models/types";
import { useCurrentUser } from "providers/CurrentUserProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApplicationService } from "services/applicationService";
import { ApplicationFilterOptions } from "services/types";
import { authoritiesIncludeGroup } from "utils/roles";

export type ApplicationListItem = {
	id: string;
	applicationProcessCode: string;
	concessionaireName: string;
	regulatoryAgencyCode: string;
	proprietorName: string;
	interventionTypeName: string;
	analysisStartDatetime: string;
	analysisLimitDatetime: string;
	highwayCode: string;
	currentResponsibleUserName: string | null;
	status: InternalUserStatusLabel;
	daysUntilDeadline: string;
	deadlinePercentage: string;
};

const headerClassName =
	"flex items-center justify-start border-0 text-xs font-semi text-neutral-low-pure-500 h-12 p-2";
const cellClassName =
	"flex items-center justify-start border-0 text-sm text-neutral-low-300 h-12 p-2";

type ApplicationListItemColumn = ColumnDef<
	ApplicationListItem,
	ApplicationListItem[keyof ApplicationListItem]
>;

const emptyFilters: ApplicationListFilters = {
	statuses: [],
	interventionTypes: [],
	concessionaires: [],
	responsibleGroups: [],
	analysts: [],
	projectProgressStatus: "",
	regulatoryAgencies: []
};

export default function BackofficeApplicationList(): JSX.Element {
	const user = useCurrentUser();
	const [urlParams, setUrlParams] = useSearchParams();

	const paginationTools = usePagination(
		LocalStorageHelper.backofficeListPageSizeKey
	);
	const { stablePage, pageItemCount, setPageCount } = paginationTools;

	const [storedSearchText, setStoredSearchText] =
		LocalStorageHelper.useLocalStorageState<string>(
			LocalStorageHelper.backofficeListSearchTextKey
		);

	const {
		setValue: setSearchText,
		stableValue: stableSearchText,
		unstableValue: searchText
	} = useStabilizedState<string>(
		urlParams.get("search") ?? storedSearchText ?? "",
		750
	);

	const searchTools = {
		setSearchText: (newSearchText: string) => {
			setSearchText(newSearchText);
			setStoredSearchText(newSearchText);
			paginationTools.setPage(Constants.INITIAL_PAGE_INDEX);
		},
		stableSearchText,
		searchText
	};

	const applicationService = useMemo(ApplicationService.getInstance, []);

	const [isLoadingFilterOptions, setIsLoadingFilterOptions] =
		useState<boolean>(false);
	const [filterOptions, setFilterOptions] =
		useState<ApplicationFilterOptions | null>(null);

	const [filters, setFilters] = useUrlFilters(
		emptyFilters,
		urlParams,
		(newParams) => {
			paginationTools.setPage(Constants.INITIAL_PAGE_INDEX);
			setUrlParams(newParams);
		},
		ApplicationService.parseBackofficeListFilterParams,
		ApplicationService.makeBackofficeApplicationFilterParams,
		LocalStorageHelper.backofficeListFiltersKey
	);
	const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
	const filterTools: FilterTools<ApplicationListFilters> = {
		filters,
		initialFilters: emptyFilters,
		resetFilters: () => {
			setFilters(emptyFilters);
		},
		setFilters,
		setModalOpen: setIsFilterModalOpen
	};

	const [items, setItems] = useState<ApplicationListItem[] | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [sorting, setSorting] = useState<SortingState>([
		{
			id: "deadline",
			desc: false
		}
	]);

	useEffect(() => {
		const newParams = new URLSearchParams(urlParams);
		newParams.set("search", stableSearchText);

		setUrlParams(newParams);
	}, [stableSearchText, urlParams]);

	const loadData = useCallback(() => {
		const sort = TableHelper.sortingStateToParam(sorting);
		setIsLoading(true);

		applicationService
			.listBackofficeApplications(
				stablePage - 1,
				pageItemCount,
				stableSearchText,
				filters,
				sort
			)
			.then((responseData) => {
				const { totalPages, content } = responseData;
				setPageCount(totalPages);
				setItems(content);
			})
			.catch((error) => ErrorHelper.showErrorMessage(error))
			.finally(() => setIsLoading(false));
	}, [
		stablePage,
		pageItemCount,
		stableSearchText,
		filters,
		setPageCount,
		setItems,
		setIsLoading,
		sorting,
		applicationService
	]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const shouldViewAgencies = useMemo(() => {
		return (
			user?.authorities &&
			authoritiesIncludeGroup(
				user?.authorities,
				Constants.UserRoleGroups.ENGENHARIA
			)
		);
	}, [user]);

	const applicationColumns: ApplicationListItemColumn[] = useMemo(() => {
		const columns: TableColumnDef<ApplicationListItem, string | null>[] = [
			{
				accessorKey: "applicationProcessCode",
				header: "Nº do processo",
				enableSorting: false,
				cell: asCellRenderer(BasicValueDisplay),
				size: 7
			},
			{
				accessorKey: "concessionaireName",
				header: "Concessionária",
				enableSorting: false,
				cell: asCellRenderer(BasicValueDisplay),
				size: 5
			},
			{
				accessorKey: "highwayCode",
				header: "Rodovia",
				enableSorting: false,
				cell: asCellRenderer(BasicValueDisplay),
				size: shouldViewAgencies ? 4 : 5
			},
			{
				accessorKey: "proprietorName",
				header: "Solicitante",
				enableSorting: false,
				cell: asCellRenderer(({ value }) => (
					<TooltipWrapper place="top" value={value?.toUpperCase()} />
				)),
				size: 7
			},
			{
				accessorKey: "interventionTypeName",
				header: "Tipo da intervenção",
				enableSorting: false,
				cell: asCellRenderer(({ value }) => (
					<TooltipWrapper place="top" value={value} />
				)),
				size: shouldViewAgencies ? 7 : 9
			},
			{
				accessorKey: "projectProgressStatus",
				header: "Etapa",
				enableSorting: false,
				cell: asCellRenderer(ProjectProgressStatusDisplay),
				size: 2
			},
			{
				accessorKey: "analysisStartDatetime",
				header: "Início da análise",
				enableSorting: false,
				cell: asCellRenderer<ApplicationListItem, "analysisStartDatetime">(
					NaiveDateValueDisplay
				),
				size: 4,
				meta: { cellClass: "justify-center", headerClass: "justify-center" }
			},
			{
				accessorKey: "deadline",
				header: "Prazo da análise",
				cell: asCellRenderer(DeadlineDisplay),
				size: 8,
				meta: { cellClass: "justify-center", headerClass: "justify-center" }
			},
			{
				accessorKey: "currentResponsibleUserName",
				header: "Analista",
				enableSorting: false,
				cell: asCellRenderer(NameValueDisplay),
				size: shouldViewAgencies ? 5 : 6
			},
			{
				accessorKey: "status",
				header: "Status",
				enableSorting: false,
				cell: asCellRenderer(ApplicationListStatusDisplay),
				size: 7
			}
		];
		if (shouldViewAgencies) {
			columns.splice(1, 0, {
				accessorKey: "regulatoryAgencyCode",
				header: "Agência",
				enableSorting: false,
				cell: asCellRenderer(BasicValueDisplay),
				size: 4
			});
		}
		return TableHelper.addClassNames(columns, cellClassName, headerClassName);
	}, []);

	return (
		<div className="h-full">
			<BackofficeList
				columns={applicationColumns}
				items={items ?? []}
				loading={isLoading}
				title="Atividades"
				paginationTools={paginationTools}
				filterTools={filterTools}
				searchTools={searchTools}
				sorting={sorting}
				setSorting={setSorting}
			/>
			{isFilterModalOpen && (
				<BackofficeApplicationListFilterForm
					setFilters={setFilters}
					clearFilters={() => {
						setFilters(emptyFilters);
						setSearchText("");
					}}
					initialFilters={filters}
					isOpen={isFilterModalOpen}
					setIsOpen={setIsFilterModalOpen}
					filterOptions={filterOptions}
					setFilterOptions={setFilterOptions}
					isLoadingFilterOptions={isLoadingFilterOptions}
					setIsLoadingFilterOptions={setIsLoadingFilterOptions}
					applicationService={applicationService}
				/>
			)}
		</div>
	);
}
